import MyNotificationItem from './components/MyNotificationItem';
import { ChevronLeft, ChevronRight, NotificationsNone } from '@mui/icons-material';
import {
  getAlarmsList,
  getNewAlarmsCount,
  readAll,
  selectAlarms,
  selectNewAlarmsCount,
} from 'app/store/alarmsSlice';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Tooltip, Badge, Button, Popover } from '@mui/material';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useUser } from 'src/app/hooks/useUser';
import handleError from '@fuse/core/errorHandler';
import axios from 'axios';
import { API } from 'app/configs/api';

const rows = 10;

const MyNotifications = () => {
  const { t } = useTranslation('translations');
  const { user } = useUser();
  const ref = useRef(null);
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);

  const dispatch = useDispatch();
  const notifications = useSelector(selectAlarms);
  const newNotificationsCount = useSelector(selectNewAlarmsCount);
  const { isDarkMode } = useSelector((state) => state.themeSlice);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const totalPages = useMemo(() => {
    if (notifications) {
      return Math.ceil(notifications.totalItems / rows);
    } else {
      return 1;
    }
  }, [notifications]);

  useEffect(() => {
    const root = document.getElementById('root');

    if (anchorEl) {
      root?.setAttribute('inert', '');
      document.body.style.overflow = 'hidden';
    } else {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    }

    return () => {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    };
  }, [anchorEl]);

  useEffect(() => {
    if (user?.id) {
      handleGetAlarms();
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [page, user]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleGetAlarms = () => {
    dispatch(
      getAlarmsList({
        filters: '{}',
        first: (page - 1) * rows,
        rows: rows,
        sortField: 'id',
        sortOrder: 0,
      })
    );
    dispatch(getNewAlarmsCount());
  };

  const handleAllRead = async () => {
    try {
      await axios.post(API.readAllNotifications);
      dispatch(readAll());
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <Button aria-describedby={id} onClick={handleClick} sx={{ padding: 0 }}>
        <Tooltip title={t('Notifications')} placement="bottom">
          <Badge badgeContent={newNotificationsCount} color="error">
            <span size="small" onClick={handleGetAlarms} sx={{ padding: 0 }}>
              <NotificationsNone className="text-grey-700 dark:text-lightgrey" />
            </span>
          </Badge>
        </Tooltip>
      </Button>
      <Popover open={open} id={id} anchorEl={anchorEl} onClose={handleClose} className="mt-32 p-12">
        <div className="w-[330px] md:w-[540px] h-[500px] overflow-y-auto flex flex-col justify-start border-2 border-white dark:bg-dark dark:text-white p-12">
          <div className="flex justify-between items-center mb-10">
            <p></p>
            <p className="text-center font-semibold">
              {notifications && notifications.items?.length === 0
                ? t('NoNotifications')
                : t('Notifications')}
            </p>
            <button
              disabled={!newNotificationsCount}
              className={`${
                newNotificationsCount ? 'cursor-pointer' : 'cursor-not-allowed'
              } bg-transparent border-0 ${!newNotificationsCount ? 'opacity-50' : ''}`}
              title={t('MarkAllAsRead')}
            >
              <DoneAllIcon onClick={handleAllRead} />
            </button>
          </div>
          <div ref={ref}>
            {notifications &&
              notifications.items?.map((notification) => (
                <MyNotificationItem key={notification.id} notification={notification} />
              ))}
          </div>
          {notifications &&
            notifications.items?.length < notifications.totalItems &&
            notifications.totalItems > rows && (
              <div>
                <div className="flex justify-center items-center">
                  <Button type="text" onClick={() => setPage(page - 1)} disabled={page < 2}>
                    <ChevronLeft style={{ color: isDarkMode ? '#fff' : '' }} />
                  </Button>
                  <span>
                    {page}/{totalPages}
                  </span>
                  <Button
                    type="text"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight style={{ color: isDarkMode ? '#fff' : '' }} />
                  </Button>
                </div>
              </div>
            )}
        </div>
      </Popover>
    </>
  );
};

export default MyNotifications;
